import { createActionGroup, createFeature, createReducer, createSelector, on, props, emptyProps } from '@ngrx/store';
import { zefActionPayload, zefActionSuccessPayload, zefActionFailPayload, zefResetState } from '@zerops/zef/core';
import {
  TwoFAMethod,
  WebAuthnLoginStartResponse,
  TOTPLoginResponse,
  TOTPRegistrationStartResponse,
  TOTPRegistrationFinishRequest,
  WebAuthnRegistrationStartResponse,
  WebAuthnRegistrationFinishRequest,
  RecoveryCodesResponse,
  Credential
} from '@zerops/models/auth';
import { TokenData, ZefAuthState } from './auth.model';
import { FEATURE_NAME } from './auth.constant';

// # Actions
export const authActions = createActionGroup({
  source: FEATURE_NAME,
  events: {
    noopAction: zefActionPayload<void>(),

    // Login flow
    'Login': zefActionPayload<{ email: string; password: string }>(),
    'Login Success': zefActionSuccessPayload<TokenData>(),
    'Login Fail': zefActionFailPayload,

    // Token management
    'Set Token': (data: TokenData, meta?: any) => ({ data, meta }),
    'Check Stored Token': (valid: boolean) => ({ valid }),
    'Check Stored Token Success': (tokenData: TokenData) => tokenData,
    'Check Stored Token Fail': emptyProps(),

    // Refresh token
    'Refresh Token': zefActionPayload<void>(),
    'Refresh Token Success': zefActionSuccessPayload<TokenData>(),
    'Refresh Token Fail': zefActionFailPayload,

    // Logout
    'Logout': (meta: { redirect?: boolean; preserveStorageToken?: boolean } = {}) => ({ meta }),
    'Api Logout': (meta: { redirect?: boolean; preserveStorageToken?: boolean } = {}) => ({ meta }),
    'Api Logout Success': emptyProps(),
    'Api Logout Fail': emptyProps(),

    // Passkey Registration (post-registration flow)
    'Register With Passkey': zefActionPayload<{
      sessionId: string;
      deviceName: string;
      registrationOptions: any;
    }>(),
    'Register With Passkey Success': zefActionSuccessPayload<{ auth: TokenData }>(),
    'Register With Passkey Fail': zefActionFailPayload,

    // 2FA Login - State-only actions
    'TwoFA Prompt Required': props<{
      methods: TwoFAMethod[];
      tempTokenData: TokenData;
    }>(),

    // 2FA Login - TOTP
    'Verify TOTP Code': zefActionPayload<{ token: string }>(),
    'Verify TOTP Code Success': zefActionSuccessPayload<TOTPLoginResponse>(),
    'Verify TOTP Code Fail': zefActionFailPayload,

    // 2FA Login - U2F/WebAuthn
    'Initiate U2F Verify': zefActionPayload<void>(),
    'Initiate U2F Verify Success': zefActionSuccessPayload<{ challenge: WebAuthnLoginStartResponse }>(),
    'Initiate U2F Verify Fail': zefActionFailPayload,

    'Submit U2F Response': zefActionPayload<{ sessionId: string; response: any }>(),
    'Submit U2F Response Success': zefActionSuccessPayload<{ auth: TokenData }>(),
    'Submit U2F Response Fail': zefActionFailPayload,

    // 2FA Login - Passkey (primary login method)
    'Passkey Login Start': zefActionPayload<void>(),
    'Passkey Login Start Success': zefActionSuccessPayload<{ challenge: WebAuthnLoginStartResponse }>(),
    'Passkey Login Start Fail': zefActionFailPayload,

    'Passkey Login Complete': zefActionPayload<{ sessionId: string; response: any }>(),
    'Passkey Login Complete Success': zefActionSuccessPayload<{ auth: TokenData }>(),
    'Passkey Login Complete Fail': zefActionFailPayload,

    'Manual Passkey Login': zefActionPayload<void>(),
    'Manual Passkey Login Success': zefActionSuccessPayload<{ challenge: WebAuthnLoginStartResponse }>(),
    'Manual Passkey Login Fail': zefActionFailPayload,

    'Cancel Passkey Login': emptyProps(),

    // 2FA Management - Credentials List
    'Load Credentials': zefActionPayload<void>(),
    'Load Credentials Success': zefActionSuccessPayload<{ list: Credential[] }>(),
    'Load Credentials Failure': zefActionFailPayload,

    // 2FA Management - TOTP Setup
    'Start TOTP Setup': zefActionPayload<void>(),
    'Start TOTP Setup Success': zefActionSuccessPayload<TOTPRegistrationStartResponse>(),
    'Start TOTP Setup Failure': zefActionFailPayload,

    'Finish TOTP Setup': zefActionPayload<TOTPRegistrationFinishRequest>(),
    'Finish TOTP Setup Success': zefActionSuccessPayload<TokenData>(),
    'Finish TOTP Setup Failure': zefActionFailPayload,

    'Remove TOTP': zefActionPayload<void>(),
    'Remove TOTP Success': zefActionSuccessPayload<void>(),
    'Remove TOTP Failure': zefActionFailPayload,

    // 2FA Management - WebAuthn Setup
    'Start WebAuthn Setup': zefActionPayload<{ residentKey: boolean }>(),
    'Start WebAuthn Setup Success': zefActionSuccessPayload<WebAuthnRegistrationStartResponse>(),
    'Start WebAuthn Setup Failure': zefActionFailPayload,

    'Finish WebAuthn Setup': zefActionPayload<WebAuthnRegistrationFinishRequest>(),
    'Finish WebAuthn Setup Success': zefActionSuccessPayload<{ auth: TokenData }>(),
    'Finish WebAuthn Setup Failure': zefActionFailPayload,

    // 2FA Management - Credentials Management
    'Delete Credential': zefActionPayload<{ id: string }>(),
    'Delete Credential Success': zefActionSuccessPayload<{ id: string }>(),
    'Delete Credential Failure': zefActionFailPayload,

    // 2FA Management - Recovery Codes
    'Load Recovery Codes': zefActionPayload<void>(),
    'Load Recovery Codes Success': zefActionSuccessPayload<RecoveryCodesResponse>(),
    'Load Recovery Codes Failure': zefActionFailPayload,

    'Generate Recovery Codes': zefActionPayload<void>(),
    'Generate Recovery Codes Success': zefActionSuccessPayload<RecoveryCodesResponse>(),
    'Generate Recovery Codes Failure': zefActionFailPayload,

    // Session cleanup (for dialog cancel/close flows)
    'Clear TOTP Setup Session': emptyProps(),
    'Clear WebAuthn Setup Session': emptyProps(),
    'Clear Recovery Codes': emptyProps(),

    // Password Management
    'Change Password': zefActionPayload<{
      oldPassword: string;
      newPassword: string;
    }>(),
    'Change Password Success': zefActionSuccessPayload<any>(),
    'Change Password Failure': zefActionFailPayload
  }
});

// # State Interface
export interface AuthFeatureState {
  // Core auth state
  authState: ZefAuthState;
  tokenData: TokenData | null;

  // 2FA login flow state
  twoFAMethods: TwoFAMethod[];
  tempTokenData: TokenData | null;
  u2fLoginChallenge: WebAuthnLoginStartResponse | null;
  passkeyLoginChallenge: WebAuthnLoginStartResponse | null;

  // 2FA management/settings state
  credentials: Credential[] | null;
  totpSetupSession: TOTPRegistrationStartResponse | null;
  webAuthnSetupSession: WebAuthnRegistrationStartResponse | null;
  recoveryCodes: RecoveryCodesResponse | null;
}

const initialState: AuthFeatureState = {
  // Core auth
  authState: ZefAuthState.Checking,
  tokenData: null,

  // 2FA login flow
  twoFAMethods: [],
  tempTokenData: null,
  u2fLoginChallenge: null,
  passkeyLoginChallenge: null,

  // 2FA management
  credentials: null,
  totpSetupSession: null,
  webAuthnSetupSession: null,
  recoveryCodes: null
};

// # Feature with Reducer
export const authFeature = createFeature({
  name: FEATURE_NAME,
  reducer: createReducer(
    initialState,

    // ## Core Auth Reducers
    on(authActions.setToken, (state, { data }) => ({
      ...state,
      authState: ZefAuthState.Authorized,
      tokenData: data
    })),

    on(authActions.checkStoredTokenSuccess, (state, tokenData) => ({
      ...state,
      authState: ZefAuthState.Authorized,
      tokenData
    })),

    on(
      authActions.checkStoredTokenFail,
      authActions.logout,
      () => ({
        ...initialState,
        authState: ZefAuthState.Invalid
      })
    ),

    on(zefResetState, () => ({
      ...initialState,
      authState: ZefAuthState.Invalid
    })),

    // ## 2FA Login Flow Reducers
    on(authActions.twoFAPromptRequired, (state, { methods, tempTokenData }) => ({
      ...state,
      twoFAMethods: methods,
      tempTokenData
    })),

    on(authActions.initiateU2FVerifySuccess, (state, { data }) => ({
      ...state,
      u2fLoginChallenge: data.challenge
    })),

    on(authActions.passkeyLoginStartSuccess, authActions.manualPasskeyLoginSuccess, (state, { data }) => ({
      ...state,
      passkeyLoginChallenge: data.challenge
    })),

    // Clear 2FA login flow state on successful auth or logout
    on(
      authActions.verifyTOTPCodeSuccess,
      authActions.submitU2FResponseSuccess,
      authActions.passkeyLoginCompleteSuccess,
      authActions.logout,
      (state) => ({
        ...state,
        twoFAMethods: [],
        tempTokenData: null,
        u2fLoginChallenge: null,
        passkeyLoginChallenge: null
      })
    ),

    // Clear passkey login challenge on cancellation
    on(authActions.cancelPasskeyLogin, (state) => ({
      ...state,
      passkeyLoginChallenge: null
    })),

    // ## 2FA Management Reducers
    on(authActions.loadCredentialsSuccess, (state, { data }) => ({
      ...state,
      credentials: data.list
    })),

    on(authActions.deleteCredentialSuccess, (state, { data }) => ({
      ...state,
      credentials: state.credentials.filter((c) => c.id !== data.id)
    })),

    on(authActions.startTOTPSetupSuccess, (state, { data }) => ({
      ...state,
      totpSetupSession: data
    })),

    on(authActions.startWebAuthnSetupSuccess, (state, { data }) => ({
      ...state,
      webAuthnSetupSession: data
    })),

    // Clear setup sessions on start failure
    on(authActions.startTOTPSetupFailure, (state) => ({
      ...state,
      totpSetupSession: null
    })),

    on(authActions.startWebAuthnSetupFailure, (state) => ({
      ...state,
      webAuthnSetupSession: null
    })),

    on(
      authActions.loadRecoveryCodesSuccess,
      authActions.generateRecoveryCodesSuccess,
      (state, { data }) => ({
        ...state,
        recoveryCodes: data
      })
    ),

    // Clear setup sessions on completion
    on(
      authActions.finishTOTPSetupSuccess,
      authActions.finishTOTPSetupFailure,
      (state) => ({
        ...state,
        totpSetupSession: null
      })
    ),

    // Only clear WebAuthn session on SUCCESS
    // On failure, keep session so user can retry in the dialog
    on(
      authActions.finishWebAuthnSetupSuccess,
      (state) => ({
        ...state,
        webAuthnSetupSession: null
      })
    ),

    // Session cleanup actions (dispatched by dialog effects on close)
    on(authActions.clearTOTPSetupSession, (state) => ({
      ...state,
      totpSetupSession: null
    })),

    on(authActions.clearWebAuthnSetupSession, (state) => ({
      ...state,
      webAuthnSetupSession: null
    })),

    on(authActions.clearRecoveryCodes, (state) => ({
      ...state,
      recoveryCodes: null
    }))
  ),
  extraSelectors: ({ selectTokenData }) => ({
    selectZefAuthAccessToken: createSelector(
      selectTokenData,
      (data) => data && data.accessToken
    )
  })
});

// # Selectors
export const {
  // Core auth selectors
  selectAuthState,
  selectTokenData,

  // 2FA login flow selectors
  selectTwoFAMethods,
  selectTempTokenData,
  selectU2fLoginChallenge,
  selectPasskeyLoginChallenge,

  // 2FA management selectors
  selectCredentials,
  selectTotpSetupSession,
  selectWebAuthnSetupSession,
  selectRecoveryCodes,

  // Extra selectors
  selectZefAuthAccessToken
} = authFeature;
